import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
  Autocomplete,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import SyncRoundedIcon from '@mui/icons-material/SyncRounded';
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import { MRT_Localization_FR } from 'material-react-table/locales/fr';
import axios from '../../../api/axiosInstance';

const emptyAccount = () => ({
  id: '',
  name: 'Compte OVH',
  endpoint: 'ovh-eu',
  enabled: true,
  applicationKey: '',
  applicationSecret: '',
  consumerKey: '',
});

function formatDate(value) {
  if (!value) return 'Jamais';
  return new Date(value).toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function OvhSettingsPage() {
  const [enabled, setEnabled] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingId, setTestingId] = useState('');
  const [syncingId, setSyncingId] = useState('');
  const [feedback, setFeedback] = useState(null);
  const [domains, setDomains] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [domainsLoading, setDomainsLoading] = useState(false);

  const hasAccounts = accounts.length > 0;

  const loadDomains = useCallback(async () => {
    setDomainsLoading(true);
    try {
      const { data } = await axios.get('/api/settings/ovh/domains');
      setOrganizations(data.organizations || []);
      setDomains((data.domains || []).map((domain) => ({
        ...domain,
        organization: domain.mappedOrganization || null,
        enabled: domain.mapping?.enabled ?? true,
        notes: domain.mapping?.notes || '',
      })));
    } catch (error) {
      setFeedback({ type: 'error', message: error.response?.data?.message || 'Impossible de charger les domaines OVH.' });
    } finally {
      setDomainsLoading(false);
    }
  }, []);

  const loadConfig = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get('/api/settings/ovh/config');
      setEnabled(Boolean(data.enabled));
      setAccounts((data.accounts || []).map((account) => ({ ...account, applicationKey: '', applicationSecret: '', consumerKey: '' })));
      await loadDomains();
    } catch (error) {
      setFeedback({ type: 'error', message: error.response?.data?.message || 'Impossible de charger OVH.' });
    } finally {
      setLoading(false);
    }
  }, [loadDomains]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const updateAccount = (index, patch) => {
    setAccounts((current) => current.map((account, i) => (i === index ? { ...account, ...patch } : account)));
  };

  const addAccount = () => {
    setAccounts((current) => [...current, emptyAccount()]);
    setEnabled(true);
  };

  const removeAccount = (index) => {
    setAccounts((current) => current.filter((_, i) => i !== index));
  };

  const saveConfig = async () => {
    setSaving(true);
    setFeedback(null);
    try {
      const { data } = await axios.put('/api/settings/ovh/config', { enabled, accounts });
      setEnabled(Boolean(data.enabled));
      setAccounts((data.accounts || []).map((account) => ({ ...account, applicationKey: '', applicationSecret: '', consumerKey: '' })));
      setFeedback({ type: 'success', message: 'Configuration OVH sauvegardée.' });
    } catch (error) {
      setFeedback({ type: 'error', message: error.response?.data?.message || 'Sauvegarde OVH impossible.' });
    } finally {
      setSaving(false);
    }
  };

  const testAccount = async (account) => {
    if (!account.id) {
      setFeedback({ type: 'warning', message: 'Sauvegarde le compte avant de lancer le test.' });
      return;
    }
    setTestingId(account.id);
    setFeedback(null);
    try {
      const { data } = await axios.post(`/api/settings/ovh/test/${account.id}`);
      setEnabled(Boolean(data.enabled));
      setAccounts((data.accounts || []).map((row) => ({ ...row, applicationKey: '', applicationSecret: '', consumerKey: '' })));
      setFeedback({ type: 'success', message: `${account.name} : ${data.domainsCount || 0} domaine(s) accessible(s).` });
    } catch (error) {
      setFeedback({ type: 'error', message: error.response?.data?.error || 'Connexion OVH impossible.' });
      if (error.response?.data?.config) {
        setEnabled(Boolean(error.response.data.config.enabled));
        setAccounts((error.response.data.config.accounts || []).map((row) => ({ ...row, applicationKey: '', applicationSecret: '', consumerKey: '' })));
      }
    } finally {
      setTestingId('');
    }
  };

  const syncAccount = async (account) => {
    if (!account.id) {
      setFeedback({ type: 'warning', message: 'Sauvegarde le compte avant de synchroniser.' });
      return;
    }
    setSyncingId(account.id);
    setFeedback(null);
    try {
      const { data } = await axios.post('/api/settings/ovh/domains/sync', { accountId: account.id });
      setFeedback({
        type: data.unmapped ? 'warning' : 'success',
        message: `Sync OVH terminée : ${data.domains || 0} domaine(s), ${data.unmapped || 0} non mappé(s), ${data.failed || 0} erreur(s).`,
      });
      await Promise.all([loadConfig(), loadDomains()]);
    } catch (error) {
      setFeedback({ type: 'error', message: error.response?.data?.message || 'Synchronisation OVH impossible.' });
    } finally {
      setSyncingId('');
    }
  };



  const updateDomain = (id, patch) => {
    setDomains((current) => current.map((domain) => (domain._id === id ? { ...domain, ...patch } : domain)));
  };

  const saveMappings = async () => {
    setSaving(true);
    setFeedback(null);
    try {
      const mappings = domains.map((domain) => ({
        domainName: domain.name,
        organization: domain.organization?._id || null,
        enabled: domain.enabled,
        notes: domain.notes || '',
      }));
      await axios.put('/api/settings/ovh/mappings', { mappings });
      setFeedback({ type: 'success', message: 'Mapping OVH sauvegardé.' });
      await loadDomains();
    } catch (error) {
      setFeedback({ type: 'error', message: error.response?.data?.message || 'Sauvegarde du mapping impossible.' });
    } finally {
      setSaving(false);
    }
  };

  const domainColumns = useMemo(() => [
    { accessorKey: 'name', header: 'Domaine', size: 220, filterVariant: 'autocomplete' },
    { accessorKey: 'ovhAccountName', header: 'Compte OVH', size: 150, filterVariant: 'autocomplete' },
    {
      accessorKey: 'organization.name',
      header: 'Client matché',
      size: 340,
      Cell: ({ row }) => (
        <Stack spacing={0.75}>
          <Autocomplete
            size="small"
            options={organizations}
            value={row.original.organization || null}
            getOptionLabel={(option) => option?.name || ''}
            isOptionEqualToValue={(option, value) => option?._id === value?._id}
            onChange={(_, value) => updateDomain(row.original._id, { organization: value, enabled: true })}
            renderInput={(params) => <TextField {...params} placeholder="Aucun client" />}
          />
          {!row.original.organization?._id && row.original.suggestedOrganization?._id ? (
            <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
              <Chip size="small" color="info" variant="outlined" label={`Suggestion : ${row.original.suggestedOrganization.name}`} />
              <Button size="small" variant="text" onClick={() => updateDomain(row.original._id, { organization: row.original.suggestedOrganization, enabled: true })}>Utiliser</Button>
              <Button size="small" color="inherit" variant="text" onClick={() => updateDomain(row.original._id, { organization: null, enabled: false })}>Ignorer</Button>
            </Stack>
          ) : null}
        </Stack>
      ),
    },
    {
      accessorKey: 'enabled',
      header: 'Mapper',
      size: 90,
      Cell: ({ row }) => <Switch checked={row.original.enabled !== false} onChange={(event) => updateDomain(row.original._id, { enabled: event.target.checked })} />,
    },
    {
      id: 'status',
      header: 'Statut',
      size: 120,
      Cell: ({ row }) => {
        if (row.original.enabled === false) return <Chip size="small" color="default" label="Ignoré" />;
        if (row.original.organization?._id) return <Chip size="small" color="success" label="Mappé" />;
        if (row.original.suggestedOrganization?._id) return <Chip size="small" color="info" variant="outlined" label="Suggestion" />;
        return <Chip size="small" color="warning" label="À mapper" />;
      },
    },
    { accessorKey: 'notes', header: 'Note', size: 220, Cell: ({ row }) => <TextField size="small" fullWidth value={row.original.notes || ''} onChange={(event) => updateDomain(row.original._id, { notes: event.target.value })} /> },
  ], [organizations]);

  const domainTable = useMaterialReactTable({
    columns: domainColumns,
    data: domains,
    localization: MRT_Localization_FR,
    enableColumnFilters: true,
    enableGlobalFilter: true,
    enableColumnFilterModes: true,
    enableColumnOrdering: true,
    enableColumnPinning: true,
    enableFacetedValues: true,
    initialState: { density: 'compact', showColumnFilters: true, showGlobalFilter: true, pagination: { pageSize: 50 } },
    state: { isLoading: domainsLoading },
  });

  const summary = useMemo(() => ({
    active: accounts.filter((account) => account.enabled !== false).length,
    configured: accounts.filter((account) => account.hasApplicationKey && account.hasApplicationSecret && account.hasConsumerKey).length,
  }), [accounts]);

  if (loading) return <Typography color="text.secondary">Chargement OVH...</Typography>;

  return (
    <Stack spacing={2.5}>
      {feedback && <Alert severity={feedback.type}>{feedback.message}</Alert>}

      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
        <Stack spacing={2}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>OVH</Typography>
              <Typography variant="body2" color="text.secondary">
                Comptes API OVH utilisés pour synchroniser les noms de domaines et leurs expirations.
              </Typography>
            </Box>
            <Chip label={enabled ? 'Activé' : 'Désactivé'} color={enabled ? 'success' : 'default'} />
          </Stack>
          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="body2">Actif</Typography>
            <Switch checked={enabled} onChange={(event) => setEnabled(event.target.checked)} />
            <Chip variant="outlined" label={`${summary.active} compte(s) actif(s)`} />
            <Chip variant="outlined" label={`${summary.configured} configuré(s)`} />
          </Stack>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button startIcon={<AddRoundedIcon />} variant="outlined" onClick={addAccount}>Ajouter un compte</Button>
            <Button startIcon={<SaveRoundedIcon />} variant="contained" onClick={saveConfig} disabled={saving || !hasAccounts}>Sauvegarder</Button>
          </Stack>
        </Stack>
      </Paper>

      {accounts.map((account, index) => (
        <Paper key={account.id || `new-${index}`} variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
              <TextField
                label="Nom"
                size="small"
                value={account.name || ''}
                onChange={(event) => updateAccount(index, { name: event.target.value })}
                sx={{ minWidth: { md: 220 } }}
              />
              <TextField
                select
                label="Endpoint"
                size="small"
                value={account.endpoint || 'ovh-eu'}
                onChange={(event) => updateAccount(index, { endpoint: event.target.value })}
                sx={{ minWidth: { md: 160 } }}
              >
                <MenuItem value="ovh-eu">OVH Europe</MenuItem>
                <MenuItem value="ovh-ca">OVH Canada</MenuItem>
                <MenuItem value="ovh-us">OVH US</MenuItem>
              </TextField>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2">Actif</Typography>
                <Switch checked={account.enabled !== false} onChange={(event) => updateAccount(index, { enabled: event.target.checked })} />
              </Stack>
              <Box sx={{ flex: 1 }} />
              <Chip
                size="small"
                label={account.lastTestStatus ? account.lastTestStatus : 'Non testé'}
                color={account.lastTestStatus === 'success' ? 'success' : account.lastTestStatus === 'error' ? 'error' : 'default'}
              />
              <IconButton color="error" onClick={() => removeAccount(index)}>
                <DeleteOutlineRoundedIcon />
              </IconButton>
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
              <TextField
                label={account.hasApplicationKey ? `Application key (${account.applicationKeyMasked})` : 'Application key'}
                size="small"
                type="password"
                value={account.applicationKey || ''}
                onChange={(event) => updateAccount(index, { applicationKey: event.target.value })}
                placeholder={account.hasApplicationKey ? 'Laisser vide pour conserver' : ''}
                sx={{ minWidth: { md: 260 } }}
              />
              <TextField
                label={account.hasApplicationSecret ? `Application secret (${account.applicationSecretMasked})` : 'Application secret'}
                size="small"
                type="password"
                value={account.applicationSecret || ''}
                onChange={(event) => updateAccount(index, { applicationSecret: event.target.value })}
                placeholder={account.hasApplicationSecret ? 'Laisser vide pour conserver' : ''}
                sx={{ minWidth: { md: 280 } }}
              />
              <TextField
                label={account.hasConsumerKey ? `Consumer key (${account.consumerKeyMasked})` : 'Consumer key'}
                size="small"
                type="password"
                value={account.consumerKey || ''}
                onChange={(event) => updateAccount(index, { consumerKey: event.target.value })}
                placeholder={account.hasConsumerKey ? 'Laisser vide pour conserver' : ''}
                sx={{ minWidth: { md: 280 } }}
              />
            </Stack>

            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
              <Button variant="outlined" onClick={() => testAccount(account)} disabled={testingId === account.id || !account.id}>Tester</Button>
              <Button startIcon={<SyncRoundedIcon />} variant="outlined" onClick={() => syncAccount(account)} disabled={syncingId === account.id || !account.id}>Synchroniser</Button>
              <Typography variant="caption" color="text.secondary">
                Dernier test : {formatDate(account.lastTestAt)} {account.lastTestMessage ? `- ${account.lastTestMessage}` : ''}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Dernière sync : {formatDate(account.lastSyncAt)} {account.lastSyncMessage ? `- ${account.lastSyncMessage}` : ''}
              </Typography>
            </Stack>
          </Stack>
        </Paper>
      ))}


      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
        <Stack spacing={1.5}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 800, flex: 1 }}>Mapping domaines OVH</Typography>
            <Chip label={`${domains.filter((domain) => domain.organization?._id).length} mappé(s)`} color="success" variant="outlined" />
            <Chip label={`${domains.filter((domain) => domain.enabled !== false && !domain.organization?._id).length} à mapper`} color="warning" variant="outlined" />
            <Button variant="outlined" onClick={loadDomains} disabled={domainsLoading}>Actualiser</Button>
            <Button variant="contained" onClick={saveMappings} disabled={saving || !domains.length}>Sauvegarder le mapping</Button>
          </Stack>
          <MaterialReactTable table={domainTable} />
        </Stack>
      </Paper>
    </Stack>
  );
}
